// The sidebar's list of pages, and the one rule for whether a person can
// open each of them.
//
// Feedback #835 (James, 2026-09-30) — moved out of components/Layout.js so the
// sidebar and the customizable quick-access bar read ONE list of pages and ONE
// "can this person open it" rule.  Before this, QuickAccessBar kept its own
// hard-coded copy of four of these entries plus its own copy of the admin
// check; a second copy is what drifts.
//
// api/src/lib/quickBarItems.js (QUICK_BAR_KEYS) is the server's allowlist of
// pickable quick-bar pages and must equal the paths below, in order.
// api/src/tests/quickBar.test.js finds this array by its declaration and
// reads its path literals to enforce that — so keep the declaration on its
// own line, the name, and the single-quoted paths.
//
// The entries are unchanged from Layout.js except Settings, whose gate is
// now 'admin_only' to match its route (see that entry).  The 2026-10 regroup
// moved entries between groups without touching any entry's gate.
//
// Group labels are also the keys of the per-person collapsed state
// (localStorage `lextrack_nav_collapsed`), so renaming a group resets that
// group to its default for everyone.

export const navGroups = [
  // 2026-10 regroup: the pages used all day come first; the configuration
  // pages that are opened a few times a year sit in FIRM SETUP, collapsed by
  // default.  Moving a page between groups never changes who can open it —
  // every entry kept its path, permission and feature gate, which
  // lib/navItems.access.test.js pins.  Items are alphabetical by name
  // within each group (lib/navItems.test.js).
  //
  // Names and icons are display-only (nothing keys on either).  2026-10
  // plain labels: /tasks is "To-Do" everywhere it is shown — sidebar, quick
  // bar, Ctrl+K, page heading, tab title (lib/todoName.test.js) — and every
  // sidebar entry has its own icon (lib/navItems.test.js).
  { label: 'DAILY WORK', items: [
    { name: 'Calendar', path: '/calendar', icon: '📅', permission: 'calendar' },
    { name: 'Clients', path: '/clients', icon: '👥', permission: 'clients' },
    { name: 'Dailies', path: '/dailies', icon: '☀️', permission: 'dailies' },
    { name: 'Dashboard', path: '/dashboard', icon: '🏠', permission: 'dashboard' },
    { name: 'Leads', path: '/leads', icon: '🌱', permission: 'leads' },
    { name: 'Matters', path: '/matters', icon: '📁', permission: 'matters' },
    // Reports OR Time Entries OR Post time (any one; Hestia via James,
    // 2026-11-02): without Reports the page is the Timesheet alone (pages/
    // Reports.js).  Same list as both App.js routes.
    { name: 'Reports',   path: '/reports',   icon: '📊', permission: ['reports', 'time_entries', 'can_post_time'] },
    { name: 'Time Entries', path: '/time-entries', icon: '⏱️', permission: 'time_entries' },
    { name: 'To-Do', path: '/tasks', icon: '✅', permission: 'tasks' },
  ]},
  { label: 'CASE MANAGEMENT', items: [
    { name: 'Communications', path: '/communications', icon: '📞', permission: 'communications', feature: 'communications' },
    { name: 'Conflicts', path: '/conflicts', icon: '🚫', permission: 'conflicts', feature: 'conflicts' },
    { name: 'Contacts', path: '/contacts', icon: '📇', permission: 'contacts' },
    { name: 'Court Rules', path: '/court-rules', icon: '🏛️', permission: 'matters', feature: 'court_rules' },
    { name: 'SOL Tracker', path: '/sol', icon: '⚖️', permission: 'sol_tracker', feature: 'sol_tracker' },
  ]},
  { label: 'BILLING & FINANCE', items: [
    { name: 'Batch Invoicing', path: '/batch-invoicing', icon: '📋', permission: 'batch_invoicing', feature: 'batch_invoicing' },
    { name: 'Billing', path: '/billing', icon: '💵', permission: 'billing_tab' },
    { name: 'Invoices', path: '/invoices', icon: '💰', permission: 'invoices' },
    { name: 'Receipts', path: '/receipts', icon: '🧾', permission: 'receipts' },
    // Listed for everyone with time_entries, whether or not the firm uses
    // time review (firm decision 2026-10: every page a person can open stays
    // in their sidebar).  Only its badge's 60 s count poll waits for review
    // to be set up — isTimeReviewConfigured() below, used by Layout and
    // hooks/useReviewQueueBadge.js.
    { name: 'Time Review Queue', path: '/time-review-queue', icon: '👁️', permission: 'time_entries' },
    { name: 'Trust Accounting', path: '/trust', icon: '🏦', permission: 'trust', feature: 'trust' },
  ]},
  { label: 'SCHEDULING', items: [
    { name: 'Consult Bookings', path: '/consult-bookings', icon: '🤝', permission: 'leads' },
    { name: 'Deadlines', path: '/deadlines', icon: '⏰', permission: 'calendar' },
    { name: 'Discovery', path: '/discovery', icon: '🔍', permission: 'matters', feature: 'discovery' },
  ]},
  { label: 'HELP', items: [
    { name: 'User Manual', path: '/help', icon: '📖', permission: null },
  ]},
  { label: 'ADMINISTRATION', items: [
    // Occasional-use tools consolidated behind a single Admin landing page
    // (was 5 separate items eating sidebar real estate).  Bookmarks + Ctrl+K
    // commands still resolve the individual routes (/audit-log, /feedback,
    // /import, /clients/merge, /time-entry-duplicates) directly.
    { name: 'Admin',     path: '/admin',     icon: '🛠️', permission: 'admin_only' },
    { name: 'Attorney Productivity', path: '/attorney-productivity', icon: '📈', permission: 'time_entries', feature: 'attorney_productivity' },
    { name: 'Custom Reports', path: '/custom-reports', icon: '📑', permission: 'reports', feature: 'custom_reports' },
    { name: 'Downloads', path: '/downloads', icon: '💾', permission: 'downloads' },
    // Both /settings routes are <AdminRoute> (App.js), and the page itself
    // refuses non-admins, so the `settings` permission alone only ever produced
    // a link that bounced to the Dashboard.  Feedback #835 (James, 2026-09-30)
    // made that worse — the same rule decides who gets a Settings button on
    // the quick bar, including one handed out by an admin's firm default.
    { name: 'Settings',  path: '/settings',  icon: '⚙️', permission: 'admin_only' },
    { name: 'Team',      path: '/team',      icon: '👨‍⚖️', permission: 'team' },
  ]},
  // Collapsed until the person opens it; Layout's SidebarNav remembers their
  // choice per group label in localStorage `lextrack_nav_collapsed` (see
  // isNavGroupCollapsed).  Payments lives here: it is the firm's Stripe
  // connection and online-payment log; recording a payment happens on
  // Receipts and the invoice pages, which stay in BILLING & FINANCE.
  { label: 'FIRM SETUP', defaultCollapsed: true, items: [
    { name: 'Accounting', path: '/accounting', icon: '🧮', permission: 'can_view_accounting' },
    { name: 'Custom Fields', path: '/custom-fields', icon: '🧩', permission: 'admin_only' },
    { name: 'Email Sequences', path: '/email-sequences', icon: '📨', permission: 'can_manage_email_sequences' },
    { name: 'Email Templates', path: '/email-templates', icon: '📧', permission: 'can_manage_email_templates' },
    { name: 'Engagement Templates', path: '/engagement-templates', icon: '📄', permission: 'can_manage_engagement_letters' },
    { name: 'Intake Forms', path: '/intake-forms', icon: '📝', permission: 'can_manage_intake_forms' },
    { name: 'Lead Assignment Rules', path: '/lead-assignment-rules', icon: '🎯', permission: 'can_manage_leads' },
    { name: 'Matter Templates', path: '/matter-templates', icon: '🗂️', permission: 'matters' },
    { name: 'Payments', path: '/payments', icon: '💳', permission: 'payments' },
    { name: 'Referral Sources', path: '/referral-sources', icon: '🔗', permission: 'leads' },
    { name: 'Roles', path: '/roles', icon: '🎭', permission: 'admin_only' },
    { name: 'Tags', path: '/tags', icon: '🏷️', permission: ['can_manage_leads', 'can_edit_contacts'] },
    { name: 'Webhooks', path: '/webhooks', icon: '🔌', permission: 'admin_only' },
  ]},
]

export const isAdmin = (user) => user?.role === 'admin' || user?.permissions?.is_admin === true

export const hasPermission = (user, permission) => {
  if (!user) return false
  if (permission === null || permission === undefined) return true  // public nav item
  if (isAdmin(user)) return true
  if (!user.permissions) return false
  // Ship 7b F2.2 — accept either a string OR array of strings (any-one-of).
  if (Array.isArray(permission)) return permission.some((p) => user.permissions[p] === true)
  return user.permissions[permission] === true
}

// Whether `item` (a navGroups entry) belongs in this person's sidebar — and,
// equally, in their quick-access bar: they hold its permission AND its firm
// feature flag is on.  `isFeatureOn` is useFeatureFlags().isEnabled.  When it
// is not supplied every feature counts as on, which matches that hook's own
// fail-soft rule (a missing flag is enabled).
export const canOpenNavItem = (user, item, isFeatureOn) => {
  if (!item) return false
  if (!hasPermission(user, item.permission)) return false
  if (!item.feature) return true
  return typeof isFeatureOn === 'function' ? isFeatureOn(item.feature) !== false : true
}

// Whether the firm uses time review, as GET /api/auth/me reports it
// (`time_review_configured`: someone active is set to require review, or an
// entry is still waiting in the queue).  It gates the Time Review Queue's
// sidebar entry (showInSidebar below) and its badge's 60 s count poll
// (Layout, hooks/useReviewQueueBadge.js) — never the page or the quick bar.
// Only an explicit `false` counts as "not set up": a user object without the
// field — an older API, or the partial /login payload AuthContext falls back
// to when /me fails — keeps the entry and the polling.
export const isTimeReviewConfigured = (user) => user?.time_review_configured !== false

// Sidebar-only rule on top of canOpenNavItem.  The firm approved hiding
// features that aren't set up yet (2026-10-12): the Time Review Queue leaves
// the sidebar while nobody is set to require review, and comes back by itself
// once someone is.  It changes nothing about access — the route still opens,
// the quick bar and both quick-bar pickers still offer it — and it stays
// drawn while the person is on that page, so the sidebar always shows where
// they are.
export const showInSidebar = (item, user, pathname) =>
  item?.path !== '/time-review-queue' ||
  isTimeReviewConfigured(user) ||
  isNavItemActive(item, pathname)

// ── Collapsed groups ────────────────────────────────────────────────────────
// What the SIDEBAR draws under each group header (Layout's SidebarNav).  None
// of it changes who can open a page: the sidebar, routes, the quick bar and
// its pickers all use canOpenNavItem().

// Whether a sidebar group is collapsed: the person's own choice when they
// have made one (`stored` is the parsed `lextrack_nav_collapsed` object,
// keyed by group label), else the group's default.  Groups default to open;
// FIRM SETUP defaults to collapsed.
export const isNavGroupCollapsed = (group, stored) => {
  if (stored && typeof stored === 'object' && Object.prototype.hasOwnProperty.call(stored, group.label)) {
    return stored[group.label] === true
  }
  return group.defaultCollapsed === true
}

// The same "is this the page I'm on" rule NavLink applies by default: the
// path itself or anything below it (/matters/123 is on Matters).
export const isNavItemActive = (item, pathname) => {
  const p = String(pathname || '')
  return p === item.path || p.startsWith(item.path + '/')
}

// The items a group draws under its header.  An open group draws them all.
// A collapsed group still draws the page the person is on, so the sidebar
// always shows where they are — without overriding the collapse they chose
// (the header stays ▸ and one click still opens or closes the whole group).
export const sidebarItemsForGroup = (items, collapsed, pathname) =>
  collapsed ? items.filter((item) => isNavItemActive(item, pathname)) : items
